import React from "react";
import {
  DashBoardDateRangePicker,
  StatsCards,
  DashboardGrossSalesChart,
  DashboardDailyMetricLineChart,
  DashboardStoreSalesCard,
  useDashboard,
  DASHBOARD_MAX_RANGE_DAYS,
  CardsLoader,
  DashboardChartLoader,
} from "..";
import { Box, Stack, Typography } from "@mui/material";
import { formatAmount } from "../../../utils";

// keeps the previous content on screen, dimmed, while a new date range loads
const RefetchDim = ({ loading, children }) => (
  <Box
    sx={{
      opacity: loading ? 0.45 : 1,
      pointerEvents: loading ? "none" : "auto",
      transition: "opacity 150ms ease",
    }}
  >
    {children}
  </Box>
);

export const DashboardUi = () => {
  const {
    state,
    isStoreAdmin,
    isApiPending,
    onChangeDate,
    handleIsOpen,
    handleDateChangeApply,
  } = useDashboard();
  // skeletons only on first load; later loads keep the current charts visible
  const showCardsSkeleton = state.isCardsLoading && !state.cardsData?.length;
  const showSalesSkeleton =
    state.isSalesGraphLoading && !state.salesGraph?.length;
  const showStoreSkeleton =
    state.isStoreSalesLoading && !state.storeSales?.length;
  return (
    <>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "16px",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            lineHeight: "24px",
            fontWeight: 600,
          }}
        >
          Overview
        </Typography>
        <DashBoardDateRangePicker
          onChangeDate={onChangeDate}
          range={state.date}
          showRange={state.showDate}
          isOpen={state.isOpen}
          handleIsOpen={handleIsOpen}
          handleDateChangeApply={handleDateChangeApply}
          isLoading={isApiPending}
          className="dashBoard-calender"
          maxRangeDays={DASHBOARD_MAX_RANGE_DAYS}
        />
      </Stack>
      {showCardsSkeleton ? (
        <CardsLoader />
      ) : (
        <RefetchDim loading={state.isCardsLoading}>
          <StatsCards data={state.cardsData} />
        </RefetchDim>
      )}
      {showSalesSkeleton ? (
        <DashboardChartLoader title="Sales by Day" />
      ) : (
        <RefetchDim loading={state.isSalesGraphLoading}>
          <DashboardGrossSalesChart
            index={3}
            data={state.salesGraph}
            title="Sales by Day"
          />
        </RefetchDim>
      )}
      {/* side by side only when each chart gets at least 480px, otherwise stacked */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 480px), 1fr))",
          columnGap: "24px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {showSalesSkeleton ? (
            <DashboardChartLoader title="ATV by Day" />
          ) : (
            <RefetchDim loading={state.isSalesGraphLoading}>
              <DashboardDailyMetricLineChart
                index={4}
                title="ATV by Day"
                data={state.salesGraph}
                yAxisKey="atv"
                seriesName="ATV"
                lineColor="#5e7360"
                valueFormatter={formatAmount}
              />
            </RefetchDim>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {showSalesSkeleton ? (
            <DashboardChartLoader title="Transactions by Day" />
          ) : (
            <RefetchDim loading={state.isSalesGraphLoading}>
              <DashboardDailyMetricLineChart
                index={5}
                title="Transactions by Day"
                data={state.salesGraph}
                yAxisKey="transaction_count"
                seriesName="Transactions"
                lineColor="#564845"
                integerValues
                valueFormatter={(value) =>
                  Math.round(Number(value) || 0).toLocaleString()
                }
              />
            </RefetchDim>
          )}
        </Box>
      </Box>
      {!isStoreAdmin &&
        (showStoreSkeleton ? (
          <DashboardChartLoader title="Sales by Store" horizontal={true} />
        ) : (
          <RefetchDim loading={state.isStoreSalesLoading}>
            <DashboardStoreSalesCard
              data={state.storeSales}
              title="Sales by Store"
            />
          </RefetchDim>
        ))}
    </>
  );
};
